import React from "react";
import { Status } from "../TaskBoard/types";
import { RichEditorHandlers } from "../RichEditor/useEditor";
import { PersonField } from "../PersonField";
import { TimelineItem, TimelineFilters } from "../Timeline/types";
import { Person as TimelinePerson } from "../CommentSection/types";
import { DateField } from "../DateField";
import { ProjectPageLayout } from "../ProjectPageLayout";
import { useTabs } from "../Tabs";
import { IconClipboardText, IconListCheck, IconLogs, IconMessage, IconMessages } from "../icons";
import { SidebarNotificationSection } from "../SidebarSection";

import { PageHeader } from "./PageHeader";
import { Overview } from "./Overview";
import { Sidebar, MobileSidebar } from "./Sidebar";
import { DeleteModal } from "./DeleteModal";
import { ProjectPermissions } from "../ProjectPage/types";
import { MoveModal } from "./MoveModal";
import { SpaceField } from "../SpaceField";
import { ProjectField } from "../ProjectField";
import type { FormattedTimePreferences } from "../FormattedTime";

export namespace TaskPage {
  export type MoveDestinationType = "project" | "space";

  export interface MoveTaskInput {
    destinationType: MoveDestinationType;
    destinationId: string;
  }

  export interface Space {
    id: string;
    name: string;
    link: string;
  }

  export type SpaceProps =
    | {
        workmapLink: string;
        space: Space;
      }
    | {
        homeLink: string;
      };

  export interface Person {
    id: string;
    fullName: string;
    avatarUrl: string | null;
    profileLink: string;
  }

  export interface Milestone {
    id: string;
    name: string;
    dueDate: DateField.ContextualDate | null;
    status: "pending" | "done";
    link?: string;
  }

  export type ReminderType = "before_due" | "due_day" | "overdue" | "on_date";

  export interface Reminder {
    type: ReminderType;
    days?: number | null;
    date?: string | null;
  }

  /** Recurrence cadence; "none" is a one-off task. Mirrors TaskBoard.TaskRecurrence (kept local to avoid a circular import). */
  export type Recurrence = "none" | "every_x_minutes" | "hourly" | "daily" | "weekly" | "monthly";

  /** Extra recurrence settings passed alongside the cadence. */
  export interface RecurrenceOptions {
    intervalMinutes?: number | null;
    workHoursOnly?: boolean;
  }

  export type TimelineItemType = TimelineItem;

  export type Props = SpaceProps & {
    // Navigation/Hierarchy
    projectName: string;
    projectLink: string;
    projectStatus: string;

    childrenCount: ProjectPageLayout.ChildrenCount;

    // Milestone selection
    milestone: Milestone | null;
    onMilestoneChange: (milestone: Milestone | null) => void;
    milestones: Milestone[];
    onMilestoneSearch: (query: string) => Promise<void>;
    hideMilestone?: boolean;

    // Core task data
    name: string;
    onNameChange: (newName: string) => Promise<boolean>;

    description: any;
    onDescriptionChange: (newDescription: any) => Promise<boolean>;

    status: Status | null;
    onStatusChange: (newStatus: Status) => void;

    statusOptions: Status[];

    dueDate?: DateField.ContextualDate;
    onDueDateChange: (newDate: DateField.ContextualDate | null) => void;
    recurrence?: Recurrence;
    recurrenceIntervalMinutes?: number | null;
    workHoursOnly?: boolean;
    onRecurrenceChange?: (recurrence: Recurrence, opts?: RecurrenceOptions) => void;
    reminders: Reminder[];
    onRemindersChange: (reminders: Reminder[]) => void | boolean | Promise<boolean>;

    assignees: Person[];
    onAssigneesChange: (newAssignees: Person[]) => void;

    // Metadata (read-only)
    createdAt: Date;
    createdBy: Person | null;
    closedAt: Date | null;

    // Subscriptions
    subscriptions: SidebarNotificationSection.Props;

    // Actions
    onDelete: () => Promise<void>;
    onDuplicate?: () => void;
    onArchive?: () => void;
    onMoveTask?: (input: MoveTaskInput) => Promise<boolean>;
    spaceSearch?: SpaceField.SearchSpaceFn;
    projectSearch?: ProjectField.SearchProjectFn;

    // Assignee selection
    assigneePersonSearch: PersonField.SearchData;
    richTextHandlers: RichEditorHandlers;
    localDraftKeyBase?: string;

    // Permissions
    permissions: ProjectPermissions;
    canEdit: boolean;
    canComment?: boolean;

    updateProjectName: (name: string) => Promise<boolean>;

    // Timeline/Activity feed
    timelineItems?: TimelineItem[];
    timelineIsLoading?: boolean;
    currentUser?: TimelinePerson;
    onAddComment: (content: any) => void;
    onEditComment: (id: string, content: any) => void;
    onDeleteComment: (id: string) => void;
    onAddReaction?: (commentId: string, emoji: string) => void | Promise<void>;
    onRemoveReaction?: (commentId: string, reactionId: string) => void | Promise<void>;
    timelineFilters?: TimelineFilters;
    formattedTimePreferences: FormattedTimePreferences;
  };

  export type ContentProps = Pick<
    Props,
    | "milestone"
    | "onMilestoneChange"
    | "milestones"
    | "onMilestoneSearch"
    | "hideMilestone"
    | "name"
    | "onNameChange"
    | "description"
    | "onDescriptionChange"
    | "status"
    | "onStatusChange"
    | "statusOptions"
    | "dueDate"
    | "onDueDateChange"
    | "recurrence"
    | "recurrenceIntervalMinutes"
    | "workHoursOnly"
    | "onRecurrenceChange"
    | "reminders"
    | "onRemindersChange"
    | "assignees"
    | "onAssigneesChange"
    | "createdAt"
    | "createdBy"
    | "subscriptions"
    | "onDelete"
    | "onArchive"
    | "onMoveTask"
    | "spaceSearch"
    | "projectSearch"
    | "assigneePersonSearch"
    | "richTextHandlers"
    | "localDraftKeyBase"
    | "canEdit"
    | "timelineItems"
    | "timelineIsLoading"
    | "currentUser"
    | "canComment"
    | "onAddComment"
    | "onEditComment"
    | "onDeleteComment"
    | "onAddReaction"
    | "onRemoveReaction"
    | "timelineFilters"
    | "formattedTimePreferences"
  >;

  export interface ContentState extends ContentProps {
    isDeleteModalOpen: boolean;
    openDeleteModal: () => void;
    closeDeleteModal: () => void;
    isMoveModalOpen: boolean;
    openMoveModal: () => void;
    closeMoveModal: () => void;
    useMinimalistDelete?: boolean;
  }

  export type State = Props & {
    isDeleteModalOpen: boolean;
    openDeleteModal: () => void;
    closeDeleteModal: () => void;
    isMoveModalOpen: boolean;
    openMoveModal: () => void;
    closeMoveModal: () => void;
  };
}

function useTaskPageState(props: TaskPage.Props): TaskPage.ContentState {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = React.useState(false);

  const deleteModalState = {
    isDeleteModalOpen,
    openDeleteModal: () => setIsDeleteModalOpen(true),
    closeDeleteModal: () => setIsDeleteModalOpen(false),
  };

  const moveModalState = {
    isMoveModalOpen,
    openMoveModal: () => setIsMoveModalOpen(true),
    closeMoveModal: () => setIsMoveModalOpen(false),
  };

  return {
    milestone: props.milestone,
    onMilestoneChange: props.onMilestoneChange,
    milestones: props.milestones,
    onMilestoneSearch: props.onMilestoneSearch,
    name: props.name,
    onNameChange: props.onNameChange,
    description: props.description,
    onDescriptionChange: props.onDescriptionChange,
    status: props.status,
    onStatusChange: props.onStatusChange,
    statusOptions: props.statusOptions,
    dueDate: props.dueDate,
    onDueDateChange: props.onDueDateChange,
    recurrence: props.recurrence,
    recurrenceIntervalMinutes: props.recurrenceIntervalMinutes,
    workHoursOnly: props.workHoursOnly,
    onRecurrenceChange: props.onRecurrenceChange,
    reminders: props.reminders,
    onRemindersChange: props.onRemindersChange,
    assignees: props.assignees,
    onAssigneesChange: props.onAssigneesChange,
    createdAt: props.createdAt,
    createdBy: props.createdBy,
    subscriptions: props.subscriptions,
    onDelete: props.onDelete,
    onArchive: props.onArchive,
    onMoveTask: props.onMoveTask,
    projectSearch: props.projectSearch,
    spaceSearch: props.spaceSearch,
    assigneePersonSearch: props.assigneePersonSearch,
    richTextHandlers: props.richTextHandlers,
    localDraftKeyBase: props.localDraftKeyBase,
    canEdit: props.canEdit,
    timelineItems: props.timelineItems,
    timelineIsLoading: props.timelineIsLoading,
    currentUser: props.currentUser,
    canComment: props.canComment,
    onAddComment: props.onAddComment,
    onEditComment: props.onEditComment,
    onDeleteComment: props.onDeleteComment,
    onAddReaction: props.onAddReaction,
    onRemoveReaction: props.onRemoveReaction,
    timelineFilters: props.timelineFilters,
    formattedTimePreferences: props.formattedTimePreferences,

    ...deleteModalState,
    ...moveModalState,
  };
}

export function TaskPage(props: TaskPage.Props) {
  const contentState = useTaskPageState(props);

  const tabs = useTabs(
    "tasks",
    [
      { id: "overview", label: "Overview", icon: <IconClipboardText size={14} /> },
      {
        id: "tasks",
        label: "Tasks",
        icon: <IconListCheck size={14} />,
        count: props.childrenCount.tasksCount,
      },
      {
        id: "check-ins",
        label: "Check-ins",
        icon: <IconMessage size={14} />,
        count: props.childrenCount.checkInsCount,
      },
      {
        id: "discussions",
        label: "Discussions",
        icon: <IconMessages size={14} />,
        count: props.childrenCount.discussionsCount,
      },
      { id: "activity", label: "Activity", icon: <IconLogs size={14} /> },
    ],
    { urlPath: props.projectLink },
  );

  return (
    <ProjectPageLayout
      {...props}
      title={[props.projectName]}
      testId="project-page"
      tabs={tabs}
      status={props.projectStatus}
    >
      <div className="flex-1 overflow-auto">
        <div className="p-4 max-w-6xl mx-auto">
          <TaskContent {...contentState} />
        </div>
      </div>
    </ProjectPageLayout>
  );
}

export function TaskContent(props: TaskPage.ContentState) {
  return (
    <>
      <PageHeader {...props} />
      <MobileSidebar {...props} />
      <div className="sm:grid sm:grid-cols-12 mt-6">
        <Overview {...props} />
        <Sidebar {...props} />
      </div>

      <MoveModal {...props} />
      <DeleteModal {...props} />
    </>
  );
}
